import { WorkspaceFrameComponent } from '@template-ui/blocks';
import { HearingWorkflowTaskPanelComponent } from "./hearing-workflow-task-panel.component";
import { HearingWorkflowTaskAuthorityService } from "./hearing-workflow-task-authority.service";
import { WorkflowLedgerApiService } from "@app/shared/services/workflow-ledger-api.service";
import { toWorkflowLedger } from "@app/shared/services/workflow-ledger.adapter";
import { catchError, of } from "rxjs";
import { HearingNavigationService, HearingNavigationView, HearingSharedView, isSharedHearingView } from "./hearing-navigation.service";
import { HEARING_DETAILS_OPEN_VALUE, HEARING_DETAILS_QUERY_PARAM, HearingPageHeaderComponent } from "./hearing-page-header.component";
import { RoleService } from "@app/shared/services/roles.service";
import { SDMF_FEATURE_FLAG } from "../hearing-record/sdmf-analysis.models";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ElementRef, Injector, OnDestroy, OnInit, afterNextRender, computed, effect, inject, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Params, Router, RouterOutlet } from "@angular/router";
import { filter } from "rxjs/operators";

import { formatRecordLabel } from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { CaseContextSnapshotRailComponent } from "@app/case-context/components/case-context-snapshot-rail/case-context-snapshot-rail.component";
import {
  HearingMilestone,
  milestoneStateTone,
} from "@app/case-context/models/hearing-milestone-overview.model";
import { WorkflowLedger, WorkflowLedgerViewState } from "@app/shared/components/workflow-ledger/workflow-ledger.models";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

import {
  HEARING_CYCLE_LINKS,
  HearingCycleLink,
  HearingPhase,
  PhmsReturnContext,
  buildReturnExtras,
  hearingCycleCommands,
  readReturnContext,
} from "../navigation/phms-detail-navigation";
import { PhmsDetailNavBarService } from "../navigation/phms-detail-nav-bar.service";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { HearingPhaseBar } from "./hearing-phase-bar.directive";
import { HearingCycleContextStore } from "./hearing-cycle-context.store";

/**
 * Persistent shell for one scheduled proceeding's hearing phases (#1530). The shell owns the page
 * header (title, status, Details & history), the route context the section editors follow, the
 * proceeding record and the shared Case Snapshot rail on the right; the routed phase page owns its
 * tabs and task-specific content. The URL fragment
 * is the tab key (`#case-review`, `#sdmf`, `#documents`) and takes precedence over a legacy
 * `?section=` deep link, which the phase page still honours (including its aliases).
 */
@Component({
  selector: "phms-hearing-phase-page",
  standalone: true,
  imports: [
    CommonModule,
    WorkspaceFrameComponent,
    RouterOutlet,
    HearingPageHeaderComponent,
    HearingWorkflowTaskPanelComponent,
    CaseContextSnapshotRailComponent,
  ],
  templateUrl: "./hearing-phase-page.component.html",
  styleUrl: "./hearing-phase-page.component.scss",
  providers: [HearingCycleContextStore, HearingPhaseBar, HearingWorkflowTaskAuthorityService],
})
export class HearingPhasePageComponent implements OnInit, OnDestroy {
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.hearingPhasePage);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly detailNav = inject(PhmsDetailNavBarService);
  private readonly contextStore = inject(HearingCycleContextStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly taskAuthority = inject(HearingWorkflowTaskAuthorityService);
  private readonly ledgerApi = inject(WorkflowLedgerApiService);

  readonly phaseBar = inject(HearingPhaseBar);
  readonly navigation = inject(HearingNavigationService);
  private readonly roles = inject(RoleService);
  private readonly canAuthor = this.roles.hasAnyPermissionSignal("inmate_offense_record.author");
  readonly phaseLinks = HEARING_CYCLE_LINKS;
  /** The routed phase, or the shared tab view (sdmf / documents) selected on that phase page. */
  readonly phase = signal<HearingNavigationView>("pre");
  /** The routed phase page itself, independent of which tab is selected. */
  readonly routePhase = signal<HearingPhase>("pre");
  readonly scheduledProceedingId = signal<number | null>(null);
  readonly legalReviewId = signal<number | null>(null);
  readonly record = this.contextStore.record;
  readonly milestoneOverview = this.contextStore.milestoneOverview;
  readonly isLoadingContext = this.contextStore.isLoading;
  readonly contextError = this.contextStore.error;
  // The person-scoped overview cannot identify this proceeding's exact workflow. Only the task
  // panel's fresh explicit link can select the workflow whose recorded history is shown here.
  private readonly workflowLedger = signal<WorkflowLedger | null>(null);
  readonly workflowLedgerState = signal<WorkflowLedgerViewState>("unavailable");
  readonly displayWorkflowLedger = this.workflowLedger.asReadonly();
  readonly workflowCurrentStepLabel = computed(() => this.workflowLedger()?.currentStepLabel ?? null);
  private returnContext: PhmsReturnContext | null = null;

  readonly incarceratedPersonId = computed(() => this.record()?.incarceratedPersonId ?? null);
  readonly phaseLabel = computed(
    () => this.phaseLinks.find((candidate) => candidate.view === this.phase())?.label ?? "Hearing Cycle",
  );
  /** Terminology-driven page title of the routed phase (e.g. "Pre-Hearing Prep"). */
  readonly pageTitle = computed(() => {
    switch (this.routePhase()) {
      case "at":
        return this.hearingText.phaseAtTitle();
      case "post":
        return this.hearingText.phasePostTitle();
      default:
        return this.hearingText.phasePreTitle();
    }
  });
  /** The proceeding's scheduling status from the hearing record (the same block the snapshot rail read). */
  readonly hearingStatus = computed(() => {
    const proceeding = this.record()?.context?.proceeding;
    const label = proceeding?.statusLabel?.trim() || formatRecordLabel(proceeding?.statusCode, "");
    return label.length > 0 ? label : null;
  });
  readonly hearingStatusCode = computed(() => this.record()?.context?.proceeding?.statusCode ?? null);
  constructor() {
    // Legacy record sections have no registered mapping to occurrence requirements. Their saved
    // fields and display statuses must not create completion or lifecycle progress.
    this.navigation.progress.set({});
    this.navigation.currentWorkflowPhase.set(null);
    effect(onCleanup => {
      const snapshot = this.taskAuthority.snapshot();
      const loading = this.taskAuthority.loading();
      const error = this.taskAuthority.error();
      const workflowId = snapshot?.link.availability === "available" ? snapshot.link.workflowInstanceId : null;
      untracked(() => {
        this.workflowLedger.set(null);
        this.workflowLedgerState.set(loading ? "loading" : "unavailable");
      });
      if (loading || error || !workflowId || !Number.isSafeInteger(workflowId) || workflowId <= 0) return;
      let current = true;
      untracked(() => this.workflowLedgerState.set("loading"));
      const request = this.ledgerApi.getLedger(workflowId).pipe(catchError(() => {
        if (current && this.taskAuthority.snapshot() === snapshot) this.workflowLedgerState.set("error");
        return of(null);
      })).subscribe(dto => {
        if (!current || this.taskAuthority.snapshot() !== snapshot || this.taskAuthority.loading()
          || this.taskAuthority.error()) return;
        if (!dto || dto.workflowInstanceId !== workflowId) {
          this.workflowLedgerState.set("error");
          return;
        }
        this.workflowLedger.set(toWorkflowLedger(dto));
        this.workflowLedgerState.set("ready");
      });
      onCleanup(() => { current = false; request.unsubscribe(); });
    });
    effect(() => this.navigation.currentWorkflowPhase.set(this.workflowCurrentStepLabel()));
    effect(() => this.navigation.structuredAnalysisEnabled.set(this.roles.isFeatureEnabled(SDMF_FEATURE_FLAG)));
    effect(() => this.navigation.privateNotesAllowed.set(this.canAuthor()));
  }

  ngOnInit(): void {
    this.returnContext = readReturnContext(readHistoryState());
    this.syncProceeding(toPositiveInt(this.route.snapshot.paramMap.get("scheduledProceedingId")));
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.syncProceeding(toPositiveInt(params.get("scheduledProceedingId"))));
    this.navigation.sectionRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(section => this.openSection(section));
    // The hearing pages carry their own header and tabs.
    this.detailNav.clear();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncProceeding(toPositiveInt(this.route.snapshot.paramMap.get("scheduledProceedingId"))));
  }

  ngOnDestroy(): void {
    this.navigation.clear();
    this.detailNav.clear();
  }

  isActivePhase(link: HearingCycleLink): boolean {
    return link.view === this.phase();
  }

  phaseCommands(link: HearingCycleLink): unknown[] {
    const proceedingId = this.scheduledProceedingId();
    return proceedingId === null ? [] : hearingCycleCommands(proceedingId, link.view);
  }

  phaseQueryParams(): Record<string, number> | null {
    const reviewId = this.legalReviewId();
    return reviewId === null ? null : { legalReview: reviewId };
  }

  phaseNavigationState(): Record<string, unknown> | undefined {
    if (this.returnContext) {
      return buildReturnExtras(this.returnContext).state;
    }
    if (this.legalReviewId() === null) {
      return undefined;
    }
    return buildReturnExtras(
      this.returnContext ?? { url: "/phms/dashboard?view=legal", label: "Operations dashboard" },
    ).state;
  }

  phaseStateLabel(link: HearingCycleLink): string {
    return this.findMilestone(link)?.stateLabel?.trim() || "Status unavailable";
  }

  phaseTone(link: HearingCycleLink): string {
    return milestoneStateTone(this.findMilestone(link)?.stateCode ?? "");
  }

  private findMilestone(link: HearingCycleLink): HearingMilestone | null {
    if (!link.milestoneCode) {
      return null;
    }
    return this.milestoneOverview()?.milestones.find(
      (milestone) => milestone.milestoneCode === link.milestoneCode,
    ) ?? null;
  }

  private loadContext(scheduledProceedingId: number): void {
    this.contextStore
      .load(scheduledProceedingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private syncProceeding(proceedingId: number | null): void {
    if (proceedingId !== this.scheduledProceedingId()) {
      this.taskAuthority.clear();
      this.workflowLedger.set(null);
      this.workflowLedgerState.set("unavailable");
      this.navigation.clear();
      this.scheduledProceedingId.set(proceedingId);
      if (proceedingId !== null) this.loadContext(proceedingId);
      else {
        this.record.set(null);
        this.milestoneOverview.set(null);
      }
    }
    this.syncRouteState();
  }

  /**
   * Opens a section on the current phase page. Phase sections and the shared SDMF / Documents views
   * become the URL fragment (the tab key); the hearing activity opens the header's Details & history.
   */
  private openSection(section: string): void {
    if (section === "activity") {
      void this.router.navigate([], {
        relativeTo: this.route, queryParams: { section: null, [HEARING_DETAILS_QUERY_PARAM]: HEARING_DETAILS_OPEN_VALUE },
        queryParamsHandling: "merge", preserveFragment: true, state: this.phaseNavigationState(),
      });
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route, fragment: section, queryParams: { section: null }, queryParamsHandling: "merge",
      state: this.phaseNavigationState(),
    });
  }

  /** A shared view the fragment can select as a tab on the phase page; SDMF only while the feature is on. */
  private sharedTabView(fragment: string | null): HearingSharedView | null {
    if (fragment === "documents") return fragment;
    if (fragment === "sdmf" && this.roles.isFeatureEnabled(SDMF_FEATURE_FLAG)) return fragment;
    return null;
  }

  private syncRouteState(): void {
    const previous = this.navigation.context();
    const routePhase = this.route.firstChild?.snapshot.data["phase"];
    if (routePhase === "pre" || routePhase === "at" || routePhase === "post") {
      this.routePhase.set(routePhase);
    }
    const fragment = this.route.snapshot.fragment ?? null;
    const sharedView = this.sharedTabView(fragment);
    this.phase.set(sharedView ?? this.routePhase());
    this.legalReviewId.set(toPositiveInt(this.route.snapshot.queryParams?.["legalReview"]));
    const proceedingId = this.scheduledProceedingId();
    const rawQueryParams: Params = this.route.snapshot.queryParams ?? {};
    const queryParams: Params = { ...rawQueryParams };
    // The fragment is the tab key; it takes precedence over a legacy ?section= value.
    if (fragment && !sharedView && !isSharedHearingView(fragment)) {
      queryParams["section"] = fragment;
    }
    if (proceedingId !== null) this.navigation.context.set({
      proceedingId, phase: this.phase(), queryParams, state: this.phaseNavigationState(),
    });
    if (proceedingId !== null && !fragment) {
      // Keep bookmarks and task shortcuts to the former shared destinations working: a legacy
      // ?section=sdmf|documents|analysis becomes that tab's fragment and ?section=activity opens
      // Details & history. Phase sections stay as they are; the phase page selects their tab.
      const requestedSection = rawQueryParams["section"];
      const legacySection = requestedSection === "analysis" ? "sdmf" : requestedSection;
      if (legacySection === "activity") {
        void this.router.navigate([], {
          relativeTo: this.route, queryParams: { section: null, [HEARING_DETAILS_QUERY_PARAM]: HEARING_DETAILS_OPEN_VALUE },
          queryParamsHandling: "merge", replaceUrl: true, state: this.phaseNavigationState(),
        });
      } else {
        const tab = this.sharedTabView(legacySection);
        if (tab) {
          void this.router.navigate([], {
            relativeTo: this.route, fragment: tab, queryParams: { section: null }, queryParamsHandling: "merge",
            replaceUrl: true, state: this.phaseNavigationState(),
          });
        }
      }
    }
    if (previous && (previous.phase !== this.phase() || previous.queryParams["section"] !== queryParams["section"])) {
      afterNextRender({ write: () => {
        const host = this.element.nativeElement;
        // Checklist shortcuts focus and reveal a specific field; retain that destination.
        const focusedTarget = host.ownerDocument.activeElement?.closest("[data-review-target]");
        if (focusedTarget && host.contains(focusedTarget)) return;
        const scroller = host.closest<HTMLElement>(".content-wrapper");
        if (scroller) scroller.scrollTop = 0;
      } }, { injector: this.injector });
    }
  }
}

function toPositiveInt(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(String(value ?? ""));
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function readHistoryState(): unknown {
  return typeof history !== "undefined" ? history.state : null;
}
